import { z } from 'zod';
import { IdsFilterQuerySchema } from '../common/ids-filter.schema';
import { refersTo } from '../common/refers-to';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const MemberStatusSchema = z.enum(['active', 'deactivated']);

export type MemberStatus = z.infer<typeof MemberStatusSchema>;

/**
 * The fixed workspace-role slugs ([ADR 0029](../../../.docs/engineering/adr/0029-rbac-permission-model.md))
 * a user may hold + be granted. Workspace authority is bound to the user, not
 * the member; `workspace-owner` / `workspace-admin` carry `grantsGlobalAccess`.
 */
export const WorkspaceRoleSlugSchema = z.enum([
  'workspace-owner',
  'workspace-admin',
  'billing',
  'hlre-admin',
  'hlre-member',
]);

export type WorkspaceRoleSlug = z.infer<typeof WorkspaceRoleSlugSchema>;

/**
 * The base-catalogue organisation-role slugs a member may hold per organisation.
 * Customisable composition (the role builder) lands with IMM-293; this slice
 * assigns from the static base set only.
 */
export const OrgRoleSlugSchema = z.enum(['organisation-owner', 'agent', 'assistant', 'viewer']);

export type OrgRoleSlug = z.infer<typeof OrgRoleSlugSchema>;

/**
 * A member's organisation role grants for one organisation (IMM-2.4 read
 * surface). A member is workspace-grained and holds roles per organisation.
 */
export const MemberOrganisationRolesSchema = z.object({
  organisationId: refersTo('organization', z.uuid()),
  roles: z.array(OrgRoleSlugSchema),
});

export type MemberOrganisationRoles = z.infer<typeof MemberOrganisationRolesSchema>;

// The plain member envelope returned by the create/update/deactivate/reactivate
// mutations (no role detail — those surfaces don't render roles).
export const WorkspaceMemberSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  status: MemberStatusSchema,
  createdAt: z.iso.datetime(),
});

// The list item is a PRINCIPAL row (IMM-2.6): the people-who-can-act list unions
// organisation members with globally-privileged workspace users ([ADR 0029] —
// a `workspace-owner`/`billing` user may hold no member row yet must appear).
// Keyed by `userId`; `memberId` is null for a privileged non-member, so the row
// carries no organisation roles and is always `active`. `lastActive` is the
// user's last session refresh (`users.refreshedAt`), null when never signed in.
export const WorkspaceMemberListItemSchema = z.object({
  // The internal user id. `z.string()` (not `z.uuid()`) mirrors
  // `AuthenticatedUserSchema.id` — user ids are opaque internal identifiers.
  userId: z.string().min(1),
  memberId: z.uuid().nullable(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  status: MemberStatusSchema,
  createdAt: z.iso.datetime(),
  lastActive: z.iso.datetime().nullable(),
  workspaceRoles: z.array(WorkspaceRoleSlugSchema),
  organisationRoles: z.array(MemberOrganisationRolesSchema),
  avatarMediaId: z.string().uuid().nullable(),
  // IMM-20.3: Standortzugehörigkeit (visibility axis).
  organizationIds: z.array(refersTo('organization', z.uuid())),
  primaryOrganizationId: refersTo('organization', z.uuid()).nullable(),
  // IMM-464.2: the Standorte this member is flagged „Erhält neue Leads" for.
  // Member-grained, like `organizationIds`, because this list is workspace-wide
  // — a caller rendering one Standort's Mitglieder tab reads
  // `leadRecipientOrganizationIds.includes(thatStandortId)`. Whether the member
  // is that Standort's Standortleitung (which forces the switch on in the UI) is
  // already derivable from `organisationRoles`, so it needs no field of its own.
  leadRecipientOrganizationIds: z.array(refersTo('organization', z.uuid())),
});

export type WorkspaceMemberListItem = z.infer<typeof WorkspaceMemberListItemSchema>;

export const SetLeadRecipientPathParamsSchema = z.object({
  organizationId: z.uuid(),
  memberId: z.uuid(),
});

// A total write of a single boolean, hence `PUT`: replaying it lands on the same
// state, so a retry after a lost response cannot flip the switch back.
export const SetLeadRecipientBodySchema = z.object({
  receivesNewLeads: z.boolean(),
});

export const SetLeadRecipientResponseSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  organisationId: refersTo('organization', z.uuid()),
  receivesNewLeads: z.boolean(),
});

export type SetLeadRecipientResponse = z.infer<typeof SetLeadRecipientResponseSchema>;

export const ListWorkspaceMembersPathParamsSchema = WorkspaceIdPathParamsSchema;

// `status` (default active) plus an optional `?ids=` batch filter (see
// IdsFilterQuerySchema) so callers can hydrate a known set of members — e.g.
// the deal surfaces resolving "Erstellt von" — without pulling the whole roster.
export const ListWorkspaceMembersQuerySchema = IdsFilterQuerySchema.extend({
  status: z.enum(['active', 'deactivated', 'all']).default('active'),
});

export const ListWorkspaceMembersResponseSchema = z.object({
  data: z.array(WorkspaceMemberListItemSchema),
});

export type ListWorkspaceMembersResponse = z.infer<typeof ListWorkspaceMembersResponseSchema>;

export const GetWorkspaceMemberPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

// A single read is always a real member, so `memberId` is non-null here (the
// list item allows null for a privileged non-member principal).
export const GetWorkspaceMemberResponseSchema = WorkspaceMemberListItemSchema.extend({
  memberId: z.uuid(),
});

export type GetWorkspaceMemberResponse = z.infer<typeof GetWorkspaceMemberResponseSchema>;

export const CreateWorkspaceMemberPathParamsSchema = WorkspaceIdPathParamsSchema;

export const CreateWorkspaceMemberBodySchema = z
  .object({
    email: z.email(),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    // IMM-20.3: the role is granted in every selected Standort (organizationIds).
    // An optional workspace role grants workspace-tier authority on the user.
    // Both are subject to the anti-escalation policy (ADR 0029).
    // A workspace without Standort roles (HLRE) invites with the workspace role alone.
    organisationRole: OrgRoleSlugSchema.optional(),
    workspaceRole: WorkspaceRoleSlugSchema.optional(),
    // Standortzugehörigkeit. Role grants are created for each selected org.
    // Empty array auto-resolves to main org when no subsidiary exists.
    organizationIds: z.array(z.uuid()).default([]),
    primaryOrganizationId: z.uuid().nullable().default(null),
  })
  .refine((body) => body.organisationRole !== undefined || body.workspaceRole !== undefined, {
    path: ['organisationRole'],
    message: 'organisationRole or workspaceRole is required',
  });

export type CreateWorkspaceMemberBody = z.infer<typeof CreateWorkspaceMemberBodySchema>;

export const CreateWorkspaceMemberResponseSchema = WorkspaceMemberSchema;

export type CreateWorkspaceMemberResponse = z.infer<typeof CreateWorkspaceMemberResponseSchema>;

// IMM-159 — invite a member into a single organisation (org-addressed). Unlike
// the workspace-addressed create, the target Standort is the path organisation,
// so the body carries no organizationIds/primaryOrganizationId. Gated on the
// org-scoped `organization:identity:member-create`, this lets a Standort-owner
// (organisation-owner) seat a member in their own location without holding
// workspace authority. No `workspaceRole`: granting workspace-tier authority
// stays a workspace-owner/admin action.
export const CreateOrganisationMemberPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const CreateOrganisationMemberBodySchema = z.object({
  email: z.email(),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  organisationRole: OrgRoleSlugSchema,
});

export type CreateOrganisationMemberBody = z.infer<typeof CreateOrganisationMemberBodySchema>;

export const CreateOrganisationMemberResponseSchema = WorkspaceMemberSchema;

export type CreateOrganisationMemberResponse = z.infer<typeof CreateOrganisationMemberResponseSchema>;

export const UpdateWorkspaceMemberPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const UpdateWorkspaceMemberBodySchema = z.object({
  email: z.email(),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  // IMM-20.3: Standortzugehörigkeit (visibility axis). Empty array triggers
  // auto-resolution to main org when no subsidiary exists.
  organizationIds: z.array(z.uuid()).default([]),
  primaryOrganizationId: z.uuid().nullable().default(null),
});

export type UpdateWorkspaceMemberBody = z.infer<typeof UpdateWorkspaceMemberBodySchema>;

export const UpdateWorkspaceMemberResponseSchema = WorkspaceMemberSchema;

export type UpdateWorkspaceMemberResponse = z.infer<typeof UpdateWorkspaceMemberResponseSchema>;

export const DeactivateWorkspaceMemberPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const DeactivateWorkspaceMemberResponseSchema = WorkspaceMemberSchema;

export type DeactivateWorkspaceMemberResponse = z.infer<typeof DeactivateWorkspaceMemberResponseSchema>;

export const ReactivateWorkspaceMemberPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const ReactivateWorkspaceMemberResponseSchema = WorkspaceMemberSchema;

export type ReactivateWorkspaceMemberResponse = z.infer<typeof ReactivateWorkspaceMemberResponseSchema>;

// IMM-2.4 — change a member's organisation role (org-addressed). The role is
// held per (member, organisation); the path organisation is the target org.
export const ChangeMemberOrganisationRolePathParamsSchema = z.object({
  organizationId: z.uuid(),
  memberId: z.uuid(),
});

export const ChangeMemberOrganisationRoleBodySchema = z.object({
  role: OrgRoleSlugSchema,
});

export type ChangeMemberOrganisationRoleBody = z.infer<typeof ChangeMemberOrganisationRoleBodySchema>;

export const ChangeMemberOrganisationRoleResponseSchema = MemberOrganisationRolesSchema;

export type ChangeMemberOrganisationRoleResponse = z.infer<typeof ChangeMemberOrganisationRoleResponseSchema>;

// Set all of a member's organisation roles at once (bulk replace, multi-role).
export const SetMemberOrganisationRolesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  memberId: z.uuid(),
});

export const SetMemberOrganisationRolesBodySchema = z.object({
  roles: z.array(OrgRoleSlugSchema).min(1),
});

export type SetMemberOrganisationRolesBody = z.infer<typeof SetMemberOrganisationRolesBodySchema>;

export const SetMemberOrganisationRolesResponseSchema = MemberOrganisationRolesSchema;

export type SetMemberOrganisationRolesResponse = z.infer<typeof SetMemberOrganisationRolesResponseSchema>;

// Remove a member from an organisation (org-addressed): delete every role the
// member holds in the addressed organisation, so it is no longer affiliated
// (Standortzugehörigkeit is derived from these role rows). Idempotent — removing
// a member that holds no role in the org is a no-op. Same authority rules as
// setOrganisationRoles apply; a member may not remove itself.
export const RemoveMemberOrganisationRolesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  memberId: z.uuid(),
});

// Echoes the addressed organisation with the member's now-empty role set, so the
// response shape matches setOrganisationRoles (MemberOrganisationRoles).
export const RemoveMemberOrganisationRolesResponseSchema = MemberOrganisationRolesSchema;

export type RemoveMemberOrganisationRolesResponse = z.infer<typeof RemoveMemberOrganisationRolesResponseSchema>;

// IMM-2.4 — change a user's workspace role (workspace-addressed). Authority is
// bound to the member's user.
export const ChangeMemberWorkspaceRolePathParamsSchema = z.object({
  workspaceId: z.uuid(),
  memberId: z.uuid(),
});

export const ChangeMemberWorkspaceRoleBodySchema = z.object({
  role: WorkspaceRoleSlugSchema,
});

export type ChangeMemberWorkspaceRoleBody = z.infer<typeof ChangeMemberWorkspaceRoleBodySchema>;

export const ChangeMemberWorkspaceRoleResponseSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  workspaceRoles: z.array(WorkspaceRoleSlugSchema),
});

export type ChangeMemberWorkspaceRoleResponse = z.infer<typeof ChangeMemberWorkspaceRoleResponseSchema>;

// Remove a user's workspace-tier role entirely (workspace-addressed): the member
// keeps its organisation roles but holds no workspace role afterwards. Rejected
// (403) on a self-target, when the target outranks the actor, or when it would
// leave the workspace without any member holding global access.
export const RemoveMemberWorkspaceRolePathParamsSchema = z.object({
  workspaceId: z.uuid(),
  memberId: z.uuid(),
});

// Echoes the member with its now-empty workspace-role set, matching the shape of
// changeWorkspaceRole.
export const RemoveMemberWorkspaceRoleResponseSchema = ChangeMemberWorkspaceRoleResponseSchema;

export type RemoveMemberWorkspaceRoleResponse = z.infer<typeof RemoveMemberWorkspaceRoleResponseSchema>;

export const ListStandortMembersPathParamsSchema = z.object({ organizationId: z.uuid() });

/**
 * One colleague at a Standort, in the shape a personalised printed item needs
 * (IMM-751.3).
 *
 * Every optional field is PRESENT and `null` when unrecorded — never omitted.
 * The consuming application has to be able to see the gap and refuse to print,
 * and it cannot do that if the key is simply missing.
 *
 * `firstName` and `lastName` are plain strings and MAY be empty: a member who
 * has never signed in has no name on the IdP-backed `User` yet. The parent PRD's
 * Fields table marks them mandatory, which is true of the key rather than of the
 * value — a stricter rule here would 500 on a record the app legitimately holds.
 */
export const StandortMemberSchema = z.object({
  memberId: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  academicTitle: z.string().nullable(),
  position: z.string().nullable(),
  mobile: z.string().nullable(),
  email: z.string(),
});

export type StandortMember = z.infer<typeof StandortMemberSchema>;

export const ListStandortMembersResponseSchema = z.object({
  members: z.array(StandortMemberSchema),
});

export type ListStandortMembersResponse = z.infer<typeof ListStandortMembersResponseSchema>;
